import type { AdminOperatorChatMessageEvent } from "../shared/interfaces/socket";
import { normalizeOperatorEmail } from "./operatorEmail";

let sharedAudioContext: AudioContext | null = null;

/**
 * Chimes stacked while AudioContext was not running (autoplay / tab suspend).
 * Flushed on the next successful gesture prime or tab resume.
 */
let pendingIncomingChimeCount = 0;

const MAX_PENDING_CHIMES = 8;
const PENDING_CHIME_SPACING_MS = 180;

function getSharedAudioContext(): AudioContext | null {
  if (typeof window === "undefined") {
    return null;
  }
  const Ctor = window.AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) {
    return null;
  }
  try {
    if (!sharedAudioContext || sharedAudioContext.state === "closed") {
      sharedAudioContext = new Ctor();
    }
    return sharedAudioContext;
  } catch {
    return null;
  }
}

/** Run after AudioContext is running; schedules a short two-tone chime. */
function playChimeOscillator(ctx: AudioContext): void {
  try {
    if (ctx.state === "closed") {
      return;
    }
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(784, t0);
    osc.frequency.setValueAtTime(988, t0 + 0.06);
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.22, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.002, t0 + 0.22);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.24);
  } catch {
    // Autoplay / AudioContext restrictions
  }
}

function takePendingChimeSlots(max: number): number {
  const n = Math.min(pendingIncomingChimeCount, max);
  pendingIncomingChimeCount -= n;
  return n;
}

function queueIncomingChime(): void {
  pendingIncomingChimeCount = Math.min(pendingIncomingChimeCount + 1, MAX_PENDING_CHIMES);
}

/**
 * If the shared context is running, plays up to `max` queued chimes (staggered).
 * Call from user-gesture priming and after successful resume.
 */
function flushPendingOperatorAdminChatChimes(max = 3): void {
  const ctx = getSharedAudioContext();
  if (!ctx || ctx.state === "closed" || ctx.state !== "running") {
    return;
  }
  const n = takePendingChimeSlots(max);
  for (let i = 0; i < n; i += 1) {
    window.setTimeout(() => {
      try {
        if (ctx.state === "running") {
          playChimeOscillator(ctx);
        }
      } catch {
        // ignore
      }
    }, i * PENDING_CHIME_SPACING_MS);
  }
}

/**
 * Inaudible tap started after resume — satisfies stricter browsers (incl. some WebKit builds)
 * that expect the audio graph to be touched during the user-gesture window, not only resume().
 */
function playSilentGraphPrimeTap(ctx: AudioContext): void {
  try {
    if (ctx.state === "closed") {
      return;
    }
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(440, t0);
    gain.gain.setValueAtTime(0.00002, t0);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.04);
  } catch {
    // ignore
  }
}

/**
 * Call after a user gesture (click/key/touch) so admin-chat chimes can play under autoplay rules.
 * Creates the AudioContext if needed, resumes it, and runs a silent tap on the destination graph.
 */
export function primeOperatorAdminChatAudioOnUserGesture(): void {
  const ctx = getSharedAudioContext();
  if (!ctx || ctx.state === "closed") {
    return;
  }
  const afterRunning = (): void => {
    playSilentGraphPrimeTap(ctx);
    flushPendingOperatorAdminChatChimes(3);
  };
  if (ctx.state === "suspended") {
    ctx.resume().then(afterRunning).catch(() => undefined);
    return;
  }
  afterRunning();
}

/**
 * When returning to the tab, resume the graph; if the browser leaves the context running,
 * play any chimes that stacked up while the socket fired in the background.
 */
export function resumeOperatorAdminChatAudioContextIfSuspended(): void {
  const ctx = getSharedAudioContext();
  if (!ctx || ctx.state === "closed") {
    return;
  }
  if (ctx.state !== "suspended") {
    flushPendingOperatorAdminChatChimes(3);
    return;
  }
  ctx
    .resume()
    .then(() => {
      requestAnimationFrame(() => {
        flushPendingOperatorAdminChatChimes(3);
      });
      window.setTimeout(() => flushPendingOperatorAdminChatChimes(3), 80);
    })
    .catch(() => undefined);
}

/**
 * Short chime when someone else posts in admin chat (Web Audio; no asset file).
 * Defers playback until `AudioContext.resume()` finishes when the context is suspended.
 */
export function playOperatorAdminChatIncomingChime(): void {
  const ctx = getSharedAudioContext();
  if (!ctx) {
    return;
  }

  const playIfRunning = (): boolean => {
    if (ctx.state === "closed") {
      return false;
    }
    if (ctx.state !== "running") {
      return false;
    }
    playChimeOscillator(ctx);
    return true;
  };

  if (playIfRunning()) {
    return;
  }

  if (ctx.state === "suspended") {
    ctx
      .resume()
      .then(() => {
        let played = false;
        const tryOnce = (): void => {
          if (played) {
            return;
          }
          if (playIfRunning()) {
            played = true;
          }
        };
        tryOnce();
        requestAnimationFrame(tryOnce);
        [16, 50, 120, 280, 600].forEach((ms) => {
          window.setTimeout(tryOnce, ms);
        });
        window.setTimeout(() => {
          if (!played) {
            queueIncomingChime();
          }
        }, 720);
      })
      .catch(() => {
        queueIncomingChime();
      });
    return;
  }

  queueIncomingChime();
}

/** Dedupe socket + polling snapshot so the same inbound message does not chime twice. */
const playedAdminChatIncomingChimeIds = new Set<string>();
const MAX_PLAYED_CHIME_IDS = 200;

function takeOldestPlayedChimeId(): string | undefined {
  const it = playedAdminChatIncomingChimeIds.values().next();
  return it.done ? undefined : it.value;
}

/**
 * Plays the admin-chat incoming chime once per logical message id (socket `message._id`
 * or notification snapshot `lastMessageId`).
 */
export function playOperatorAdminChatIncomingChimeIfFresh(soundDedupeId: string): void {
  const key = String(soundDedupeId || "").trim();
  if (!key) {
    playOperatorAdminChatIncomingChime();
    return;
  }
  if (playedAdminChatIncomingChimeIds.has(key)) {
    return;
  }
  playedAdminChatIncomingChimeIds.add(key);
  while (playedAdminChatIncomingChimeIds.size > MAX_PLAYED_CHIME_IDS) {
    const oldest = takeOldestPlayedChimeId();
    if (oldest == null) break;
    playedAdminChatIncomingChimeIds.delete(oldest);
  }
  playOperatorAdminChatIncomingChime();
}

export function shouldPlayAdminChatIncomingSound(
  detail: AdminOperatorChatMessageEvent,
  viewerEmail: string,
  isSuperAdmin: boolean,
): boolean {
  if (detail.isReactionUpdate) {
    return false;
  }
  const row = detail.message;
  if (!row || !viewerEmail) {
    return false;
  }
  const from = normalizeOperatorEmail(row.fromEmail);
  const viewer = normalizeOperatorEmail(viewerEmail);
  if (!from || !viewer || from === viewer) {
    return false;
  }

  if (isSuperAdmin) {
    return row.fromRole === "operator";
  }

  const fromOperatorField = normalizeOperatorEmail(row.operatorEmail || "");
  const threadKeyOp = typeof row.threadKey === "string"
    ? normalizeOperatorEmail(row.threadKey.split("::")[0] || "")
    : "";
  const threadOperator = fromOperatorField || threadKeyOp;
  return Boolean(threadOperator && threadOperator === viewer);
}
